import { Box, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { AvatarImage, DEFAULT_AVATAR } from "@outschool/ui-components-shared";
import { BoxButton, Space } from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React, { PropsWithChildren } from "react";

import {
  GetLearnerModeTransferTokenMutation,
  GetLearnerModeTransferTokenMutationVariables
} from "../../../shared/generated/graphql";
import ActionType from "../../actions/ActionType";
import { useCurrentUserLearners } from "../../hooks/Learners";
import { getLearnerModeTransferToken } from "../../queries/AuthenticationQueries";
import { useAppState } from "../../stores/AppStateProvider";

interface LearnerModeLinksProps {
  onClick: () => void;
  hideSwitchText?: boolean;
}

function LearnerModeLinks({ onClick, hideSwitchText }: LearnerModeLinksProps) {
  const { t } = useTranslation("client\\components\\nav\\LearnerModeLinks");
  const { currentUser } = useSession();
  const appState = useAppState();
  const learners = useCurrentUserLearners();

  const [getTransferToken] = useMutation<
    GetLearnerModeTransferTokenMutation,
    GetLearnerModeTransferTokenMutationVariables
  >(getLearnerModeTransferToken);

  const handleLearnerClick = async (learnerUid: string) => {
    onClick();
    const { data } = await getTransferToken({ variables: { learnerUid } });
    appState.appDispatcher.dispatch(ActionType.User.REDIRECT_TO_LEARNER_APP, {
      transferToken: data?.learnerModeTransferToken?.transferToken,
      authRequirement: currentUser?.learnerModeSwitchAuthRequirement
    });
  };

  return (
    <Box>
      {!hideSwitchText && (
        <Typography
          variant="inherit"
          sx={{
            fontWeight: 500,
            color: "grey.700",
            marginX: "1em"
          }}
        >
          {t`Switch to:`}
        </Typography>
      )}
      {_.sortBy(learners, l => l.name).map(learner => (
        <SwitcherLink
          key={learner.uid}
          onClick={() => handleLearnerClick(learner.uid)}
        >
          <AvatarImage
            avatar={learner.avatar || DEFAULT_AVATAR}
            width={24}
            height={24}
            name={learner.name}
          />
          <Space x="small" />
          <Typography
            variant="inherit"
            title={learner.name}
            sx={{
              wordBreak: "break-all"
            }}
          >
            {learner.name}
          </Typography>
        </SwitcherLink>
      ))}
    </Box>
  );
}

interface SwitcherLinkProps {
  onClick: () => void;
}
const SwitcherLink: React.FC<PropsWithChildren<SwitcherLinkProps>> = ({
  onClick,
  children
}) => (
  <BoxButton
    onClick={onClick}
    sx={{
      display: "flex",
      px: "medium",
      py: "small",
      cursor: "pointer",
      alignItems: "center",
      "&:hover": {
        backgroundColor: "headerBorder"
      }
    }}
  >
    {children}
  </BoxButton>
);

export default LearnerModeLinks;
